import { ContractorFormValues } from '../containers';

type ValidationErrors = Record<string, string[] | undefined>;

const CAPACITY_FIELDS = [
  { toggle: 'hangerAvailable', capacity: 'hangerCapacity', label: 'Hanger' },
  { toggle: 'finisherAvailable', capacity: 'finisherCapacity', label: 'Finisher' },
  { toggle: 'sprayerAvailable', capacity: 'sprayerCapacity', label: 'Sprayer' },
  { toggle: 'trimAvailable', capacity: 'trimCapacity', label: 'Trim' },
  { toggle: 'paintAvailable', capacity: 'paintCapacity', label: 'Paint' },
  { toggle: 'bumperAvailable', capacity: 'bumperCapacity', label: 'Bumper' },
] as const;

function validateRate(
  value: number | null,
  label: string,
): string[] | undefined {
  if (!value) return [`The ${label} is required`];
  if (value < 1) return [`The ${label} must be at least 1`];
  if (value > 100) return [`The ${label} must be 100 or less`];
  return undefined;
}

function validateCapacity(
  value: number | null,
  label: string,
): string[] | undefined {
  if (value == null) {
    return [`The ${label} capacity is required when ${label} is enabled`];
  }
  if (!Number.isInteger(value)) {
    return [`The ${label} capacity must be a whole number`];
  }
  if (value < 0) {
    return [`The ${label} capacity must be 0 or a positive number`];
  }
  return undefined;
}

export type ContractorValidationOptions = {
  /** Regional Admins must give a Contractor at least one Region (SuperAdmin: optional). */
  requireRegion?: boolean;
};

export const validateContractor = async (
  values: ContractorFormValues,
  options: ContractorValidationOptions = {},
): Promise<ValidationErrors> => {
  const errors: ValidationErrors = {};

  if (options.requireRegion && !(values.selectedRegions ?? []).length) {
    errors.selectedRegions = ['The Region is required'];
  }

  if (!values.name) errors.name = ['The name is required'];
  if (!values.corporation) errors.corporation = ['The corporation is required'];
  if (!values.paymentType) errors.paymentType = ['The Payment Type is required'];

  const paymentRateError = validateRate(values.paymentRate, 'Payment Rate');
  if (paymentRateError) errors.paymentRate = paymentRateError;

  if (values.bumperAvailable) {
    // Bumper Payment Type is deliberately NOT validated here: it is read-only
    // for capacity-only roles (Regional Manager), so an inline error would
    // block a save on a field they cannot fix. The rule is enforced where the
    // user can act on it instead — at toggle-on in ResourceTypeTable and again
    // at submit via getMissingPaymentTypeMessage.
    const bumperRateError = validateRate(
      values.bumperPaymentRate,
      'Bumper Payment Rate',
    );
    if (bumperRateError) errors.bumperPaymentRate = bumperRateError;
  } else {
    if (values.bumperPaymentType) {
      errors.bumperPaymentType = [
        'The Bumper Payment Type is not allowed unless a Bumper is available',
      ];
    }
    if (values.bumperPaymentRate) {
      errors.bumperPaymentRate = [
        'The Bumper Payment Rate is not allowed unless a Bumper is available',
      ];
    }
  }

  for (const { toggle, capacity, label } of CAPACITY_FIELDS) {
    if (!values[toggle]) continue;
    const capacityError = validateCapacity(values[capacity], label);
    if (capacityError) errors[capacity] = capacityError;
  }

  return errors;
};
